import { BubbleMenu, Editor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import {
  Popover,
  IconButton,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import PropTypes from 'prop-types';
import useTextMenuContentTypes from '../../hooks/useTextMenuContentTypes';
import useTextMenuCommands from '../../hooks/useTextMenuCommands';
import useTextMenuStates from '../../hooks/useTextMenuStates';
import EditLinkPopover from './editLinkPopover';

const TextMenu = ({ editor }) => {
  const [selecting, setSelecting] = useState(false);
  const [anchorElHighlight, setAnchorElHighlight] = useState(null);
  const [anchorElColor, setAnchorElColor] = useState(null);
  const [anchorElMore, setAnchorElMore] = useState(null);

  const commands = useTextMenuCommands(editor);
  const states = useTextMenuStates(editor);
  const blockOptions = useTextMenuContentTypes(editor);

  useEffect(() => {
    const controller = new AbortController();
    let selectionTimeout;

    document.addEventListener(
      'selectionchange',
      () => {
        setSelecting(true);
        if (selectionTimeout) clearTimeout(selectionTimeout);
        selectionTimeout = setTimeout(() => setSelecting(false), 500);
      },
      { signal: controller.signal },
    );

    return () => controller.abort();
  }, []);

  return (
    <BubbleMenu
      className={`${selecting ? 'hidden' : ''}`}
      editor={editor}
      pluginKey="textMenu"
      shouldShow={states.shouldShow}
      updateDelay={0}
      tippyOptions={{
        popperOptions: {
          placement: 'top-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8 },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
              },
            },
          ],
        },
        offset: [0, 8],
        maxWidth: 'calc(100vw - 16px)',
      }}
    >
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 flex flex-wrap items-center gap-2 max-w-4xl transition-all duration-200">
        <FormControl size="small" className="min-w-[120px]">
          <Select
            native
            value={states.currentBlock || ''}
            onChange={(e) => {
              const selectedOption = blockOptions.find((opt) => opt.id === e.target.value);
              if (selectedOption && selectedOption.onClick) {
                selectedOption.onClick();
              }
            }}
            placeholder="Select Type"
          >
            <option value="" disabled hidden>
              Select type
            </option>

            {blockOptions
              .filter((opt) => opt.type === 'option')
              .map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
          </Select>
        </FormControl>

        <FormControl size="small" className="min-w-[120px]">
          <InputLabel shrink>Font</InputLabel>
          <Select
            native
            value={states.currentFont || ''}
            onChange={(e) => commands.onSetFont(e.target.value)}
          >
            <option value="">Default</option>
            <option value="Arial">Arial</option>
            <option value="Georgia">Georgia</option>
            <option value="Times New Roman">Times New Roman</option>
          </Select>
        </FormControl>

        <FormControl size="small" className="min-w-[100px]">
          <InputLabel shrink>Size</InputLabel>
          <Select
            native
            value={states.currentSize || ''}
            onChange={(e) => commands.onSetFontSize(e.target.value)}
          >
            <option value="">Default</option>
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
            <option value="24px">24</option>
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Bold (Ctrl+B)">
          <IconButton onClick={commands.onBold} color={states.isBold ? 'primary' : 'default'}>
            <Icons.FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic (Ctrl+I)">
          <IconButton onClick={commands.onItalic} color={states.isItalic ? 'primary' : 'default'}>
            <Icons.FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline (Ctrl+U)">
          <IconButton
            onClick={commands.onUnderline}
            color={states.isUnderline ? 'primary' : 'default'}
          >
            <Icons.FormatUnderlined />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough (Ctrl+Shift+S)">
          <IconButton onClick={commands.onStrike} color={states.isStrike ? 'primary' : 'default'}>
            <Icons.FormatStrikethrough />
          </IconButton>
        </Tooltip>
        <Tooltip title="Code (Ctrl+E)">
          <IconButton onClick={commands.onCode} color={states.isCode ? 'primary' : 'default'}>
            <Icons.Code />
          </IconButton>
        </Tooltip>
        <Tooltip title="Code Block">
          <IconButton onClick={commands.onCodeBlock}>
            <Icons.InsertDriveFile />
          </IconButton>
        </Tooltip>

        <EditLinkPopover onSetLink={commands.onLink} />

        <Tooltip title="Highlight">
          <IconButton
            onClick={(e) => setAnchorElHighlight(e.currentTarget)}
            color={states.currentHighlight ? 'primary' : 'default'}
          >
            <Icons.Highlight />
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(anchorElHighlight)}
          anchorEl={anchorElHighlight}
          onClose={() => setAnchorElHighlight(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <div className="p-3 bg-white border rounded-lg shadow space-y-2 w-40 modal-safe-click-zone">
            <p className="text-sm block mb-2">Pick highlight color:</p>
            <input
              type="color"
              onChange={(e) => {
                commands.onChangeHighlight(e.target.value);
                setAnchorElHighlight(null);
              }}
              className="w-full h-10 cursor-pointer"
            />
            <button
              type="button"
              onClick={commands.onClearHighlight}
              className="text-red-500 hover:text-red-600 text-sm rounded w-full px-3 py-1 mt-2"
            >
              Clear
            </button>
          </div>
        </Popover>

        <Tooltip title="Text Color">
          <IconButton
            onClick={(e) => setAnchorElColor(e.currentTarget)}
            color={states.currentColor ? 'primary' : 'default'}
          >
            <Icons.Palette />
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(anchorElColor)}
          anchorEl={anchorElColor}
          onClose={() => setAnchorElColor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          className="modal-safe-click-zone"
        >
          <div className="p-3 bg-white border rounded-lg shadow space-y-2 w-40 ">
            <p className="text-sm block mb-2">Pick text color:</p>
            <input
              tabIndex={-1}
              type="color"
              onChange={(e) => {
                commands.onChangeColor(e.target.value);
                setTimeout(() => setAnchorElColor(null), 10); // Delay prevents scroll jump
              }}
              className="w-full h-10 cursor-pointer "
            />
            <button
              type="button"
              onClick={commands.onClearColor}
              className="text-red-500 hover:text-red-600 text-sm rounded w-full px-3 py-1 mt-2"
            >
              Clear
            </button>
          </div>
        </Popover>

        <Tooltip title="More">
          <IconButton onClick={(e) => setAnchorElMore(e.currentTarget)}>
            <Icons.MoreVert />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorElMore}
          open={Boolean(anchorElMore)}
          onClose={() => setAnchorElMore(null)}
          className="modal-safe-click-zone"
        >
          <Divider />
          <MenuItem onClick={commands.onAlignLeft} selected={states.isAlignLeft}>
            <Icons.FormatAlignLeft fontSize="small" />
            &nbsp; Align Left
          </MenuItem>
          <MenuItem onClick={commands.onAlignCenter} selected={states.isAlignCenter}>
            <Icons.FormatAlignCenter fontSize="small" />
            &nbsp; Align Center
          </MenuItem>
          <MenuItem onClick={commands.onAlignRight} selected={states.isAlignRight}>
            <Icons.FormatAlignRight fontSize="small" />
            &nbsp; Align Right
          </MenuItem>
          <MenuItem onClick={commands.onAlignJustify} selected={states.isAlignJustify}>
            <Icons.FormatAlignJustify fontSize="small" />
            &nbsp; Justify
          </MenuItem>
        </Menu>
      </div>
    </BubbleMenu>
  );
};

TextMenu.propTypes = {
  editor: PropTypes.instanceOf(Editor).isRequired,
};

export default TextMenu;
